import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Tabs,
  Table,
  Checkbox,
  Text,
  Loader,
  Center,
  Badge,
  Menu,
  Button,
  Modal,
  Group,
  Stack,
  Title,
  TextInput,
  Paper,
  SimpleGrid,
  ThemeIcon,
  ScrollArea,
  CloseButton,
  Avatar,
  Select,
  Pagination,
} from "@mantine/core";



import {
  IconUserCheck,
  IconClipboardCheck,
  IconCoin,
  IconSearch,
  IconMail,
  IconUsers,
  IconClockHour4,
  IconCircleCheck,
  IconCircleX,
  IconFilterOff,
  IconFileUpload,
  IconFile,
  IconFileOff,
  IconTemplate,
  IconDownload,
} from "@tabler/icons-react";


import { FileButton } from "@mantine/core";



import { ApiService } from "../api/api-service";

// Shared axios instance (already configured with baseURL + auth token
// interceptor in api-service.ts) — matches the ApiService.getInstance()
// pattern used elsewhere in this codebase.
const apiClient = ApiService.getInstance();

// =====================================================================
// Types
// =====================================================================

interface Trainer {
  email: string;
  name: string;
  allowed_referral: string; // "" | "yes"| "no"
  earned_reward_points: number;
}

interface Referral {
  referral_id: string;
  batch_no: string;
  trainer_email: string;
  referral_date: string;
  referral_timestamp: string;
  course:string;
  status: "Pending" | "Approved" | "Rejected";
}



interface RedemptionRecord {
  redemption_id: string;
  created_at: string;
  redemption_amount: number;
  redemption_status: "unpaid" | "paid";
}

interface TrainerWithRedemptions {
  email: string;
  name: string;
  redemptions: RedemptionRecord[];
}


// Base path for this module's API — adjust if your router is mounted elsewhere
const BASE_PATH = "/trainer-referral";

// Shared page size for all paginated tables in this module
const PAGE_SIZE = 25;

// =====================================================================
// Small shared helpers
// =====================================================================

function getInitials(name: string) {
  if (!name) return "?";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
}

// `dense` keeps the avatar smaller/tighter for the Trainer tab without
// touching font sizes anywhere, and without changing the default
// appearance used by Review/Points tabs.
function NameCell({ name, dense = false }: { name: string; dense?: boolean }) {
  return (
    <Group gap={dense ? 6 : "sm"} wrap="nowrap">
      <Avatar radius="xl" size={dense ? 24 : "sm"} color="indigo" variant="light">
        {getInitials(name)}
      </Avatar>
      <Text size="sm" fw={500}>
        {name}
      </Text>
    </Group>
  );
}

// `dense` tightens padding/gaps only — TextInput `size` stays "sm" so
// font size is unchanged from the original.
//
// Added optional `selectValue` / `selectData` / `onSelectChange` props so
// this same bar can host a dropdown filter (Allowed Referral, Status,
// etc.) alongside the existing Name/Email text filters, reused across
// the Trainer, Review, and Points tabs.
function FilterBar({
  nameValue,
  emailValue,
  onNameChange,
  onEmailChange,
  onClear,
  resultCount,
  totalCount,
  dense = false,
  nameLabel = "Filter by name...",
  emailLabel = "Filter by email...",
  selectValue,
  selectData,
  selectPlaceholder,
  onSelectChange,
  selectWidth = 170,
}: {
  nameValue: string;
  emailValue: string;
  onNameChange: (v: string) => void;
  onEmailChange: (v: string) => void;
  onClear: () => void;
  resultCount: number;
  totalCount: number;
  dense?: boolean;
  nameLabel?: string;
  emailLabel?: string;
  selectValue?: string;
  selectData?: { value: string; label: string }[];
  selectPlaceholder?: string;
  onSelectChange?: (v: string | null) => void;
  selectWidth?: number;
}) {
  const hasSelectFilter = !!selectValue && selectValue !== "all";
  const hasFilters = nameValue.trim() !== "" || emailValue.trim() !== "" || hasSelectFilter;

  return (
    <Paper withBorder radius="md" p={dense ? 6 : "sm"} mb={dense ? "xs" : "md"} bg="gray.0">
      <Group justify="space-between" wrap="wrap" gap={dense ? 6 : "sm"}>
        <Group gap={dense ? 6 : "sm"} wrap="wrap" style={{ flex: 1 }}>
          <TextInput
            placeholder={nameLabel}
            leftSection={<IconSearch size={14} />}
            value={nameValue}
            onChange={(e) => onNameChange(e.currentTarget.value)}
            rightSection={
              nameValue ? (
                <CloseButton size="xs" onClick={() => onNameChange("")} aria-label="Clear name filter" />
              ) : null
            }
            radius="md"
            size="sm"
            style={{ minWidth: 220 }}
          />
          <TextInput
            placeholder={emailLabel}
            leftSection={<IconMail size={14} />}
            value={emailValue}
            onChange={(e) => onEmailChange(e.currentTarget.value)}
            rightSection={
              emailValue ? (
                <CloseButton size="xs" onClick={() => onEmailChange("")} aria-label="Clear email filter" />
              ) : null
            }
            radius="md"
            size="sm"
            style={{ minWidth: 240 }}
          />
          {selectData && onSelectChange && (
            <Select
              placeholder={selectPlaceholder || "Filter"}
              data={selectData}
              value={selectValue || "all"}
              onChange={onSelectChange}
              radius="md"
              size="sm"
              allowDeselect={false}
              style={{ minWidth: selectWidth }}
            />
          )}
          {hasFilters && (
            <Button
              variant="subtle"
              color="gray"
              size="sm"
              radius="md"
              leftSection={<IconFilterOff size={14} />}
              onClick={onClear}
            >
              Clear
            </Button>
          )}
        </Group>
        <Text size="xs" c="dimmed" fw={500}>
          Showing {resultCount} of {totalCount}
        </Text>
      </Group>
    </Paper>
  );
}

function EmptyState({ icon, message }: { icon: React.ReactNode; message: string }) {
  return (
    <Center py={60}>
      <Stack align="center" gap={6}>
        <ThemeIcon size={48} radius="xl" variant="light" color="gray">
          {icon}
        </ThemeIcon>
        <Text c="dimmed" size="sm">
          {message}
        </Text>
      </Stack>
    </Center>
  );
}

function LoadingState() {
  return (
    <Center py={60}>
      <Stack align="center" gap={8}>
        <Loader size="md" color="indigo" />
        <Text size="xs" c="dimmed">
          Loading...
        </Text>
      </Stack>
    </Center>
  );
}

// =====================================================================
// MAIN COMPONENT
// =====================================================================

export default function TrainerReferral() {
  const [activeTab, setActiveTab] = useState<string | null>("trainer");

  return (
    <Box>
      <Group justify="space-between" mb="lg" wrap="wrap">
        <Box>
          <Title order={3}>Trainer Referral</Title>
        </Box>
      </Group>

      <Tabs
        value={activeTab}
        onChange={setActiveTab}
        keepMounted={false}
        variant="pills"
        radius="md"
      >
        <Tabs.List mb="md">
          <Tabs.Tab value="trainer" leftSection={<IconUserCheck size={16} />}>
            Trainer
          </Tabs.Tab>
          <Tabs.Tab value="review" leftSection={<IconClipboardCheck size={16} />}>
            Review
          </Tabs.Tab>
          <Tabs.Tab value="points" leftSection={<IconCoin size={16} />}>
            Points
          </Tabs.Tab>

<Tabs.Tab value="template-upload" leftSection={<IconTemplate size={16} />}>
  Template Upload
</Tabs.Tab>


        </Tabs.List>

        <Tabs.Panel value="trainer">
          <TrainerTab />
        </Tabs.Panel>

        <Tabs.Panel value="review">
          <ReviewTab />
        </Tabs.Panel>

        <Tabs.Panel value="points">
          <PointsTab />
        </Tabs.Panel>


<Tabs.Panel value="template-upload">
  <TemplateUploadTab />
</Tabs.Panel>



      </Tabs>
    </Box>
  );
}

// =====================================================================
// TAB 1: TRAINER
// =====================================================================

const ALLOWED_REFERRAL_OPTIONS = [
  { value: "all", label: "All" },
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

function TrainerTab() {
  const [trainers, setTrainers] = useState<Trainer[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingEmail, setUpdatingEmail] = useState<string | null>(null);
  const [nameFilter, setNameFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [allowedReferralFilter, setAllowedReferralFilter] = useState("all");

  // Pagination
  const [activePage, setActivePage] = useState(1);

  // Confirm-before-toggle modal state
  const [confirmToggle, setConfirmToggle] = useState<{
    email: string;
    checked: boolean;
  } | null>(null);
  const [toggling, setToggling] = useState(false);

  const fetchTrainers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`${BASE_PATH}/trainers`);
      setTrainers(res.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch trainers:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrainers();
  }, [fetchTrainers]);

  const handleToggleAllowedReferral = async (email: string, checked: boolean) => {
    const newValue = checked ? "yes" : "no";

    // Optimistic update
    setTrainers((prev) =>
      prev.map((t) => (t.email === email ? { ...t, allowed_referral: newValue } : t)),
    );
    setUpdatingEmail(email);

    try {
      await apiClient.patch(`${BASE_PATH}/trainers/${encodeURIComponent(email)}/allowed-referral`, {
        allowed_referral: newValue,
      });
    } catch (err) {
      console.error("Failed to update allowed_referral:", err);
      // Revert on failure
      setTrainers((prev) =>
        prev.map((t) =>
          t.email === email ? { ...t, allowed_referral: checked ? "no" : "yes" } : t,
        ),
      );
    } finally {
      setUpdatingEmail(null);
    }
  };

  // Called from the checkbox — just opens the confirmation modal instead
  // of toggling immediately.
  const handleRequestToggle = (email: string, checked: boolean) => {
    setConfirmToggle({ email, checked });
  };

  // Called when the user confirms in the modal — this actually performs
  // the update (optimistic update + API call) via the function above.
  const handleConfirmToggle = async () => {
    if (!confirmToggle) return;
    setToggling(true);
    try {
      await handleToggleAllowedReferral(confirmToggle.email, confirmToggle.checked);
    } finally {
      setToggling(false);
      setConfirmToggle(null);
    }
  };

  const filteredTrainers = useMemo(() => {
    const nameQ = nameFilter.trim().toLowerCase();
    const emailQ = emailFilter.trim().toLowerCase();
    return trainers.filter((t) => {
      const nameMatch = nameQ ? t.name?.toLowerCase().includes(nameQ) : true;
      const emailMatch = emailQ ? t.email?.toLowerCase().includes(emailQ) : true;
      const allowedMatch =
        allowedReferralFilter === "all" ? true : t.allowed_referral === allowedReferralFilter;
      return nameMatch && emailMatch && allowedMatch;
    });
  }, [trainers, nameFilter, emailFilter, allowedReferralFilter]);

  // Reset to page 1 whenever any filter changes so the user doesn't land
  // on an empty page.
  useEffect(() => {
    setActivePage(1);
  }, [nameFilter, emailFilter, allowedReferralFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredTrainers.length / PAGE_SIZE));

  const paginatedTrainers = useMemo(() => {
    const start = (activePage - 1) * PAGE_SIZE;
    return filteredTrainers.slice(start, start + PAGE_SIZE);
  }, [filteredTrainers, activePage]);

  const allowedCount = useMemo(
    () => trainers.filter((t) => t.allowed_referral === "yes").length,
    [trainers],
  );

  if (loading) return <LoadingState />;

  return (
    <Box>
      {/* Stat cards: 2-up even on mobile, tighter padding, smaller icon
          box. Text sizes (xs / xl) are unchanged from the original. */}
      <SimpleGrid cols={2} mb="xs" spacing="xs">
        <Paper withBorder radius="md" p="xs">
          <Group justify="space-between" wrap="nowrap" gap="xs">
            <Box>
              <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                Total Trainers
              </Text>
              <Text size="xl" fw={700}>
                {trainers.length}
              </Text>
            </Box>
            <ThemeIcon size={28} radius="md" variant="light" color="indigo">
              <IconUsers size={16} />
            </ThemeIcon>
          </Group>
        </Paper>
        <Paper withBorder radius="md" p="xs">
          <Group justify="space-between" wrap="nowrap" gap="xs">
            <Box>
              <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                Referral Enabled
              </Text>
              <Text size="xl" fw={700}>
                {allowedCount}
              </Text>
            </Box>
            <ThemeIcon size={28} radius="md" variant="light" color="green">
              <IconUserCheck size={16} />
            </ThemeIcon>
          </Group>
        </Paper>
      </SimpleGrid>

      <FilterBar
        nameValue={nameFilter}
        emailValue={emailFilter}
        onNameChange={setNameFilter}
        onEmailChange={setEmailFilter}
        onClear={() => {
          setNameFilter("");
          setEmailFilter("");
          setAllowedReferralFilter("all");
        }}
        resultCount={filteredTrainers.length}
        totalCount={trainers.length}
        dense
        selectValue={allowedReferralFilter}
        selectData={ALLOWED_REFERRAL_OPTIONS}
        selectPlaceholder="Allowed Referral"
        onSelectChange={(v) => setAllowedReferralFilter(v || "all")}
      />

      {trainers.length === 0 ? (
        <EmptyState icon={<IconUsers size={24} />} message="No trainers found." />
      ) : filteredTrainers.length === 0 ? (
        <EmptyState icon={<IconSearch size={24} />} message="No trainers match your filters." />
      ) : (
        <>
          <Paper withBorder radius="md">
            <ScrollArea>
              <Table verticalSpacing={6} horizontalSpacing="sm" highlightOnHover>
                <Table.Thead bg="gray.0">
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Email</Table.Th>
                    <Table.Th ta="center" w={140}>
                      Allowed Referral
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {paginatedTrainers.map((trainer) => (
                    <Table.Tr key={trainer.email}>
                      <Table.Td>
                        <NameCell name={trainer.name} dense />
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="black">
                          {trainer.email}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="center">
                        <Checkbox
                          checked={trainer.allowed_referral === "yes"}
                          disabled={updatingEmail === trainer.email}
                          onChange={(e) =>
                            handleRequestToggle(trainer.email, e.currentTarget.checked)
                          }
                        />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Paper>

          {totalPages > 1 && (
            <Group justify="center" mt="md">
              <Pagination total={totalPages} value={activePage} onChange={setActivePage} />
            </Group>
          )}
        </>
      )}

      {/* Confirm allowed_referral toggle */}
      <Modal
        opened={!!confirmToggle}
        onClose={() => setConfirmToggle(null)}
        title={<Text fw={600}>Confirm Change</Text>}
        centered
        radius="md"
      >
        <Stack gap="sm">
          <Text size="sm">
            Are you sure you want to set allowed referral to{" "}
            <Text span fw={600}>
              {confirmToggle?.checked ? "Yes" : "No"}
            </Text>{" "}
            for{" "}
            <Text span fw={600}>
              {confirmToggle?.email}
            </Text>
            ?
          </Text>
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setConfirmToggle(null)} disabled={toggling}>
              Cancel
            </Button>
            <Button color="indigo" onClick={handleConfirmToggle} loading={toggling}>
              Confirm
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}

// =====================================================================
// TAB 2: REVIEW
// =====================================================================

const REFERRAL_STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "Pending", label: "Pending" },
  { value: "Approved", label: "Approved" },
  { value: "Rejected", label: "Rejected" },
];

// Builds a sortable/comparable timestamp from the separate date + time
// fields stored on each referral. Falls back gracefully if either field
// is missing or not parseable.
function getReferralSortValue(r: Referral): number {
  const combined = `${r.referral_date || ""} ${r.referral_timestamp || ""}`.trim();
  const t = new Date(combined).getTime();
  if (!isNaN(t)) return t;

  // Fallback: try the date alone
  const dateOnly = new Date(r.referral_date).getTime();
  return isNaN(dateOnly) ? 0 : dateOnly;
}
// Wraps a value in quotes and escapes inner quotes so commas, quotes
// and line breaks in the data can't break the CSV structure.
function escapeCsvValue(value: unknown): string {
  const str = value === null || value === undefined ? "" : String(value);
  return `"${str.replace(/"/g, '""')}"`;
}

function downloadReferralsCsv(rows: Referral[]) {
  const headers = [
    "Referral ID",
    "Batch No",
    "Trainer Email",
    "Referral Date",
    "Referral Time",
    "Status",
  ];

  const lines = rows.map((r) =>
    [
      r.referral_id,
      r.batch_no,
      r.trainer_email,
      r.referral_date,
      r.referral_timestamp,
      r.course,
      r.status,
    ]
      .map(escapeCsvValue)
      .join(","),
  );

  const csv = [headers.map(escapeCsvValue).join(","), ...lines].join("\n");

  // "\uFEFF" (BOM) makes Excel read the file as UTF-8 correctly
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const today = new Date().toISOString().slice(0, 10);
  const link = document.createElement("a");
  link.href = url;
  link.download = `referral-review-${today}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}


function ReviewTab() {
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState<{
    referral: Referral;
    action: "Approved" | "Rejected";
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [nameFilter, setNameFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Date filter — expects "YYYY-MM-DD" from a native <input type="date">
  const [dateFilter, setDateFilter] = useState("");

  // Pagination
  const [activePage, setActivePage] = useState(1);

  const fetchReferrals = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`${BASE_PATH}/referrals`);
      const data: Referral[] = res.data?.data || [];

      // Show the latest referral first
      const sorted = [...data].sort(
        (a, b) => getReferralSortValue(b) - getReferralSortValue(a),
      );

      setReferrals(sorted);
    } catch (err) {
      console.error("Failed to fetch referrals:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  const handleConfirmAction = async () => {
    if (!confirmModal) return;
    const { referral, action } = confirmModal;

    setSubmitting(true);
    try {
      await apiClient.patch(`${BASE_PATH}/referrals/${referral.referral_id}/status`, {
        status: action,
        trainer_email: referral.trainer_email,
      });

      setReferrals((prev) =>
        prev.map((r) => (r.referral_id === referral.referral_id ? { ...r, status: action } : r)),
      );
      setConfirmModal(null);
    } catch (err) {
      console.error("Failed to update referral status:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const statusColor = (status: Referral["status"]) => {
    if (status === "Approved") return "green";
    if (status === "Rejected") return "red";
    return "yellow";
  };

  const statusIcon = (status: Referral["status"]) => {
    if (status === "Approved") return <IconCircleCheck size={14} />;
    if (status === "Rejected") return <IconCircleX size={14} />;
    return <IconClockHour4 size={14} />;
  };

  // this tab's only person-identifying field is trainer_email,
  // so "Name" filter matches against batch number while "Email" matches
  // against trainer email — this keeps both filters meaningful here.
  const filteredReferrals = useMemo(() => {
    const nameQ = nameFilter.trim().toLowerCase();
    const emailQ = emailFilter.trim().toLowerCase();
    return referrals.filter((r) => {
      const batchMatch = nameQ ? r.batch_no?.toLowerCase().includes(nameQ) : true;
      const emailMatch = emailQ ? r.trainer_email?.toLowerCase().includes(emailQ) : true;
      const statusMatch = statusFilter === "all" ? true : r.status === statusFilter;

      let dateMatch = true;
      if (dateFilter) {
        const referralDate = new Date(r.referral_date);
        if (!isNaN(referralDate.getTime())) {
          // Compare using local date parts so timezone offset doesn't
          // shift a referral onto the "wrong" day.
          const y = referralDate.getFullYear();
          const m = String(referralDate.getMonth() + 1).padStart(2, "0");
          const d = String(referralDate.getDate()).padStart(2, "0");
          dateMatch = `${y}-${m}-${d}` === dateFilter;
        } else {
          // If referral_date can't be parsed as a Date, fall back to a
          // plain string-prefix match (handles "YYYY-MM-DD..." strings).
          dateMatch = r.referral_date?.startsWith(dateFilter) ?? false;
        }
      }

      return batchMatch && emailMatch && statusMatch && dateMatch;
    });
  }, [referrals, nameFilter, emailFilter, statusFilter, dateFilter]);

  // Reset to page 1 whenever any filter changes.
  useEffect(() => {
    setActivePage(1);
  }, [nameFilter, emailFilter, statusFilter, dateFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredReferrals.length / PAGE_SIZE));

  const paginatedReferrals = useMemo(() => {
    const start = (activePage - 1) * PAGE_SIZE;
    return filteredReferrals.slice(start, start + PAGE_SIZE);
  }, [filteredReferrals, activePage]);

  const counts = useMemo(() => {
    return {
      pending: referrals.filter((r) => r.status === "Pending").length,
      approved: referrals.filter((r) => r.status === "Approved").length,
      rejected: referrals.filter((r) => r.status === "Rejected").length,
    };
  }, [referrals]);

   const handleDownloadCsv = () => {
    downloadReferralsCsv(filteredReferrals);
  };


  if (loading) return <LoadingState />;

  return (
    <Box>
      <SimpleGrid cols={{ base: 1, sm: 3 }} mb="md" spacing="sm">
        <Paper withBorder radius="md" p="md">
          <Group justify="space-between">
            <Box>
              <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                Pending
              </Text>
              <Text size="xl" fw={700}>
                {counts.pending}
              </Text>
            </Box>
            <ThemeIcon size={40} radius="md" variant="light" color="yellow">
              <IconClockHour4 size={20} />
            </ThemeIcon>
          </Group>
        </Paper>
        <Paper withBorder radius="md" p="md">
          <Group justify="space-between">
            <Box>
              <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                Approved
              </Text>
              <Text size="xl" fw={700}>
                {counts.approved}
              </Text>
            </Box>
            <ThemeIcon size={40} radius="md" variant="light" color="green">
              <IconCircleCheck size={20} />
            </ThemeIcon>
          </Group>
        </Paper>
        <Paper withBorder radius="md" p="md">
          <Group justify="space-between">
            <Box>
              <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                Rejected
              </Text>
              <Text size="xl" fw={700}>
                {counts.rejected}
              </Text>
            </Box>
            <ThemeIcon size={40} radius="md" variant="light" color="red">
              <IconCircleX size={20} />
            </ThemeIcon>
          </Group>
        </Paper>
      </SimpleGrid>

      <FilterBar
        nameValue={nameFilter}
        emailValue={emailFilter}
        onNameChange={setNameFilter}
        onEmailChange={setEmailFilter}
        onClear={() => {
          setNameFilter("");
          setEmailFilter("");
          setStatusFilter("all");
          setDateFilter("");
        }}
        resultCount={filteredReferrals.length}
        totalCount={referrals.length}
        nameLabel="Filter by batch no..."
        emailLabel="Filter by trainer email..."
        selectValue={statusFilter}
        selectData={REFERRAL_STATUS_OPTIONS}
        selectPlaceholder="Status"
        onSelectChange={(v) => setStatusFilter(v || "all")}
      />

      {/* Date filter — filters referrals created on the selected day */}
{/* Date filter + CSV download */}
<Group mb="md" gap="sm" align="flex-end" justify="space-between">
  <Group gap="sm" align="flex-end">
    <TextInput
      type="date"
      label="Filter by referral date"
      value={dateFilter}
      onChange={(e) => setDateFilter(e.currentTarget.value)}
      size="sm"
      radius="md"
      style={{ maxWidth: 200 }}
    />
    {dateFilter && (
      <Button variant="subtle" color="gray" size="xs" onClick={() => setDateFilter("")}>
        Clear date
      </Button>
    )}
  </Group>

  <Button
    variant="light"
    color="indigo"
    size="sm"
    radius="md"
    leftSection={<IconDownload size={16} />}
    onClick={handleDownloadCsv}
    disabled={referrals.length === 0}
  >
    Download CSV
  </Button>
</Group>


      {referrals.length === 0 ? (
        <EmptyState icon={<IconClipboardCheck size={24} />} message="No referrals found." />
      ) : filteredReferrals.length === 0 ? (
        <EmptyState icon={<IconSearch size={24} />} message="No referrals match your filters." />
      ) : (
        <>
          <Paper withBorder radius="md">
            <ScrollArea>
              <Table verticalSpacing="sm" highlightOnHover>
                <Table.Thead bg="gray.0">
                  <Table.Tr>
                    <Table.Th>Batch No</Table.Th>
                    <Table.Th ta="center">Trainer Email</Table.Th>
                    <Table.Th ta="center">Referral Date</Table.Th>
                    <Table.Th ta="center">Referral Time</Table.Th>
                    <Table.Th ta="center">Course</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {paginatedReferrals.map((referral) => (
                    <Table.Tr key={referral.referral_id}>
                      <Table.Td>
                        <Badge variant="outline" color="gray" radius="sm">
                          {referral.batch_no}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{referral.trainer_email}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {referral.referral_date}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {referral.referral_timestamp}
                        </Text>
                      </Table.Td>

                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {referral.course}
                        </Text>
                      </Table.Td>

                      <Table.Td>
                        {referral.status === "Pending" ? (
                          <Menu shadow="md" width={160} withinPortal>
                            <Menu.Target>
                              <Badge
                                color={statusColor(referral.status)}
                                leftSection={statusIcon(referral.status)}
                                style={{ cursor: "pointer" }}
                              >
                                {referral.status}
                              </Badge>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item
                                color="green"
                                leftSection={<IconCircleCheck size={14} />}
                                onClick={() => setConfirmModal({ referral, action: "Approved" })}
                              >
                                Approve
                              </Menu.Item>
                              <Menu.Item
                                color="red"
                                leftSection={<IconCircleX size={14} />}
                                onClick={() => setConfirmModal({ referral, action: "Rejected" })}
                              >
                                Reject
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        ) : (
                          <Badge
                            color={statusColor(referral.status)}
                            leftSection={statusIcon(referral.status)}
                          >
                            {referral.status}
                          </Badge>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Paper>

          {totalPages > 1 && (
            <Group justify="center" mt="md">
              <Pagination total={totalPages} value={activePage} onChange={setActivePage} />
            </Group>
          )}
        </>
      )}

      {/* Confirmation Modal */}
      <Modal
        opened={!!confirmModal}
        onClose={() => setConfirmModal(null)}
        title={
          <Text fw={600}>
            {confirmModal?.action === "Approved" ? "Approve" : "Reject"} Referral
          </Text>
        }
        centered
        radius="md"
      >
        <Stack gap="sm">
          <Text size="sm">
            Are you sure you want to{" "}
            <Text span fw={600}>
              {confirmModal?.action === "Approved" ? "approve" : "reject"}
            </Text>{" "}
            this referral from{" "}
            <Text span fw={600}>
              {confirmModal?.referral.trainer_email}
            </Text>
            ?
          </Text>
          {confirmModal?.action === "Approved" && (
            <Paper bg="green.0" p="xs" radius="sm">
              <Text size="xs" c="green.8">
                This will add 20 reward points to the trainer's total.
              </Text>
            </Paper>
          )}
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setConfirmModal(null)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              color={confirmModal?.action === "Approved" ? "green" : "red"}
              onClick={handleConfirmAction}
              loading={submitting}
            >
              Confirm {confirmModal?.action === "Approved" ? "Approve" : "Reject"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}

// =====================================================================
// TAB 3: POINTS
// =====================================================================

function formatDateTime(iso: string) {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString(),
    time: d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
  };
}

function PointsTab() {
  const [trainers, setTrainers] = useState<TrainerWithRedemptions[]>([]);
  const [loading, setLoading] = useState(true);
  const [payModal, setPayModal] = useState<{
    email: string;
    record: RedemptionRecord;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [nameFilter, setNameFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");

  const fetchTrainers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`${BASE_PATH}/points`);
      setTrainers(res.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch redemption history:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrainers();
  }, [fetchTrainers]);

  const handleConfirmPaid = async () => {
    if (!payModal) return;
    const { email, record } = payModal;

    setSubmitting(true);
    try {
      await apiClient.patch(
        `${BASE_PATH}/trainers/${encodeURIComponent(email)}/redemption/${record.redemption_id}/status`,
      );

      setTrainers((prev) =>
        prev.map((t) =>
          t.email === email
            ? {
                ...t,
                redemptions: t.redemptions.map((r) =>
                  r.redemption_id === record.redemption_id
                    ? { ...r, redemption_status: "paid" }
                    : r,
                ),
              }
            : t,
        ),
      );
      setPayModal(null);
    } catch (err) {
      console.error("Failed to update redemption status:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredTrainers = useMemo(() => {
    const nameQ = nameFilter.trim().toLowerCase();
    const emailQ = emailFilter.trim().toLowerCase();
    return trainers.filter((t) => {
      const nameMatch = nameQ ? t.name?.toLowerCase().includes(nameQ) : true;
      const emailMatch = emailQ ? t.email?.toLowerCase().includes(emailQ) : true;
      return nameMatch && emailMatch;
    });
  }, [trainers, nameFilter, emailFilter]);

  if (loading) return <LoadingState />;

  if (trainers.length === 0) {
    return <EmptyState icon={<IconCoin size={24} />} message="No redemption history found." />;
  }

  return (
    <Box>
      <FilterBar
        nameValue={nameFilter}
        emailValue={emailFilter}
        onNameChange={setNameFilter}
        onEmailChange={setEmailFilter}
        onClear={() => {
          setNameFilter("");
          setEmailFilter("");
        }}
        resultCount={filteredTrainers.length}
        totalCount={trainers.length}
      />

      {filteredTrainers.length === 0 ? (
        <EmptyState icon={<IconSearch size={24} />} message="No trainers match your filters." />
      ) : (
        <Stack gap="md">
          {filteredTrainers.map((trainer) => (
            <Paper key={trainer.email} withBorder radius="md" p="md">
              <Group mb="sm">
                <NameCell name={trainer.name} />
                <Text size="xs" c="dimmed">
                  {trainer.email}
                </Text>
              </Group>

              <Table verticalSpacing="sm" highlightOnHover>
                <Table.Thead bg="gray.0">
                  <Table.Tr>
                    <Table.Th>Created At</Table.Th>
                    <Table.Th>Amount</Table.Th>
                    <Table.Th ta="center">Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {trainer.redemptions.map((record) => {
                    const { date, time } = formatDateTime(record.created_at);
                    return (
                      <Table.Tr key={record.redemption_id}>
                        <Table.Td>
                          <Text size="sm">{date}</Text>
                          <Text size="xs" c="dimmed">
                            {time}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light" color="orange" radius="sm" size="lg">
                            ₹{record.redemption_amount}
                          </Badge>
                        </Table.Td>
                        <Table.Td ta="center">
                          {record.redemption_status === "unpaid" ? (
                            <Button
                              size="xs"
                              color="yellow"
                              variant="filled"
                              onClick={() => setPayModal({ email: trainer.email, record })}
                            >
                              Unpaid
                            </Button>
                          ) : (
                            <Badge color="green" leftSection={<IconCircleCheck size={14} />}>
                              Paid
                            </Badge>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Paper>
          ))}
        </Stack>
      )}

      <Modal
        opened={!!payModal}
        onClose={() => setPayModal(null)}
        title={<Text fw={600}>Pay to Trainer</Text>}
        centered
        radius="md"
      >
        <Stack gap="sm">
          <Text size="sm">
            Mark this redemption of{" "}
            <Text span fw={600}>
              ₹{payModal?.record.redemption_amount}
            </Text>{" "}
            as paid?
          </Text>
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setPayModal(null)} disabled={submitting}>
              Cancel
            </Button>
            <Button color="green" onClick={handleConfirmPaid} loading={submitting}>
              Paid
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}


// =====================================================================
// TAB 4: TEMPLATE UPLOAD
// =====================================================================

interface TemplateFile {
  key: string;
  fileName: string;
  url: string;
  size: number;
  lastModified?: string;
}

function formatFileSize(bytes: number) {
  if (!bytes) return "0 KB";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function TemplateUploadTab() {
  const [templates, setTemplates] = useState<TemplateFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [deleteModal, setDeleteModal] = useState<TemplateFile | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`${BASE_PATH}/templates`);
      setTemplates(res.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch templates:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleUpload = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    setUploadError("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      await apiClient.post(`${BASE_PATH}/templates/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await fetchTemplates();
    } catch (err: any) {
      console.error("Failed to upload template:", err);
      setUploadError(err?.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await apiClient.delete(`${BASE_PATH}/templates/${encodeURIComponent(deleteModal.key)}`);
      setTemplates((prev) => prev.filter((t) => t.key !== deleteModal.key));
      setDeleteModal(null);
    } catch (err) {
      console.error("Failed to delete template:", err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Text size="sm" c="dimmed">
          Upload reusable referral template files (PPT, PPTX, DOC, DOCX, PDF).
        </Text>
        <FileButton
          onChange={handleUpload}
          accept=".ppt,.pptx,.doc, .png, .docx,.pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
        >
          {(props) => (
            <Button {...props} leftSection={<IconFileUpload size={16} />} loading={uploading}>
              Upload Template
            </Button>
          )}
        </FileButton>
      </Group>

      {uploadError && (
        <Paper bg="red.0" p="xs" radius="sm" mb="sm">
          <Text size="xs" c="red.8">
            {uploadError}
          </Text>
        </Paper>
      )}

      {loading ? (
        <LoadingState />
      ) : templates.length === 0 ? (
        <EmptyState icon={<IconFileOff size={24} />} message="No templates uploaded yet." />
      ) : (
        <Paper withBorder radius="md">
          <ScrollArea>
            <Table verticalSpacing="sm" highlightOnHover>
              <Table.Thead bg="gray.0">
                <Table.Tr>
                  <Table.Th>File Name</Table.Th>
                  <Table.Th ta="center">Size</Table.Th>
                  <Table.Th ta="center">Uploaded</Table.Th>
                  <Table.Th ta="center" w={160}>
                    Actions
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {templates.map((tpl) => (
                  <Table.Tr key={tpl.key}>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <IconFile size={16} />
                        <Text size="sm">{tpl.fileName}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td ta="center">
                      <Text size="xs" c="dimmed">
                        {formatFileSize(tpl.size)}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="center">
                      <Text size="xs" c="dimmed">
                        {tpl.lastModified ? new Date(tpl.lastModified).toLocaleString() : "—"}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="center">
                      <Group gap={6} justify="center">
                        <Button
                          size="xs"
                          variant="light"
                          color="indigo"
                          component="a"
                          href={tpl.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download
                        </Button>
                        <Button
                          size="xs"
                          variant="light"
                          color="red"
                          onClick={() => setDeleteModal(tpl)}
                        >
                          Delete
                        </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>
      )}

      <Modal
        opened={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        title={<Text fw={600}>Delete Template</Text>}
        centered
        radius="md"
      >
        <Stack gap="sm">
          <Text size="sm">
            Are you sure you want to delete{" "}
            <Text span fw={600}>
              {deleteModal?.fileName}
            </Text>
            ? This cannot be undone.
          </Text>
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setDeleteModal(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button color="red" onClick={handleConfirmDelete} loading={deleting}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}